import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Lock, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { signInWithGoogle, signInWithEmailPassword, registerWithEmailPassword } from '../../utils/firebase';
import { trackFunnelStep1, trackAuthentication } from '../../utils/analytics';

interface SignupStepProps {
  onContinue: () => void;
}

export default function SignupStep({ onContinue }: SignupStepProps) {
  const { user } = useAuth();
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [isSignUp, setIsSignUp] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    // Track page view when component mounts
    trackFunnelStep1.viewAccountCreation();
  }, []);
  
  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSigningIn) return;
    
    setIsSigningIn(true);
    setError('');
    
    try {
      if (isSignUp) {
        await registerWithEmailPassword(email, password);
        trackFunnelStep1.createAccount();
        trackAuthentication('email', true);
      } else {
        await signInWithEmailPassword(email, password);
        trackFunnelStep1.clickSignIn();
        trackAuthentication('email', true);
      }
      onContinue();
    } catch (error: any) {
      trackAuthentication('email', false, error.message);
      // Handle specific error cases
      if (error.code === 'auth/invalid-credential' || error.code === 'auth/user-not-found') {
        setError(`No account found with this email. Would you like to create one?`);
        setIsSignUp(true);
      } else if (error.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists. Please sign in instead.');
        setIsSignUp(false);
      } else if (error.code === 'auth/weak-password') {
        setError('Password should be at least 6 characters long.');
      } else {
        setError(error.message || 'Authentication failed');
      }
      console.error('Auth error:', error);
    } finally {
      setIsSigningIn(false);
    }
  };
  
  const handleGoogleSignIn = async () => {
    if (isSigningIn) return;
    
    setIsSigningIn(true);
    setError('');
    
    try {
      trackFunnelStep1.clickGoogleLogin();
      await signInWithGoogle();
      trackAuthentication('google', true);
      onContinue();
    } catch (error: any) {
      trackAuthentication('google', false, error.message);
      setError(error.message || 'Failed to sign in with Google');
      console.error('Google sign in error:', error);
    } finally {
      setIsSigningIn(false);
    }
  };
  
  return (
    <div className="p-8 md:p-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-8 font-gabarito">
          {isSignUp ? 'Create your account' : 'Sign in to your account'}
        </h2>

        {/* Google Sign In Button */}
        <div className="max-w-md mx-auto mb-8">
          <button
            onClick={handleGoogleSignIn}
            disabled={isSigningIn}
            className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors relative"
          >
            <img 
              src="https://cdn4.iconfinder.com/data/icons/social-media-2210/24/Google-512.png" 
              className="w-5 h-5 absolute left-4"
              alt="Google"
            />
            <span className="text-slate font-medium">
              {isSigningIn ? 'Signing in...' : 'Continue with Google'}
            </span>
          </button>
        </div>

        {/* Divider */}
        <div className="max-w-md mx-auto relative mb-8">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">
              or continue with email
            </span>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="max-w-md mx-auto mb-6">
            <div className="bg-red-50 border-l-4 border-red-500 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Email Sign In Form */}
        <form onSubmit={handleEmailAuth} className="max-w-md mx-auto space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email address
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                placeholder="you@example.com"
              />
            </div>
          </div>
          
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete={isSignUp ? "new-password" : "current-password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                placeholder="••••••••"
                minLength={6}
              />
            </div>
            {isSignUp && (
              <p className="mt-1 text-xs text-gray-500">
                Must be at least 6 characters
              </p>
            )}
          </div>
          
          <button
            type="submit"
            disabled={isSigningIn}
            className="w-full flex justify-center items-center px-4 py-3 border border-transparent rounded-lg shadow-sm text-white bg-purple-gradient hover:bg-purple-gradient-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 transition-all duration-300"
          >
            {isSigningIn ? 'Processing...' : (isSignUp ? 'Create Account' : 'Sign In')}
          </button>
        </form>
        
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => {
              setIsSignUp(!isSignUp);
              setError('');
              if (isSignUp) {
                trackFunnelStep1.clickSignIn();
              }
            }}
            className="text-sm text-primary hover:text-primary-hover"
          >
            {isSignUp ? 'Already have an account? Sign in' : 'Need an account? Create one'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}